import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CROP_ASPECT, type CropRect, type CropSource } from "../lib/image.ts";

/** Four times in is well past the point a phone photograph holds up. */
const MAX_ZOOM = 4;
/** One arrow press shifts the view this fraction of the frame. */
const NUDGE = 0.04;
const ZOOM_STEP = 0.12;

interface ImageCropperProps {
  source: CropSource;
  /** The treatment name, so the studio can see what it is cropping for. */
  subject: string;
  busy: boolean;
  /** Shown here rather than behind the backdrop, so a failed upload keeps the crop. */
  error: string | null;
  onCancel: () => void;
  onConfirm: (rect: CropRect) => void;
}

/** How the studio is looking at the source: how far in, and at what. */
interface View {
  zoom: number;
  centre: { x: number; y: number };
}

function clamp(value: number, low: number, high: number): number {
  // low can exceed high by a rounding hair when the crop fills an axis exactly.
  return low >= high ? (low + high) / 2 : Math.min(high, Math.max(low, value));
}

/** The largest 16:10 rectangle that fits inside the source: zoom 1. */
function widestCrop(source: CropSource): { width: number; height: number } {
  const width = Math.min(source.width, source.height * CROP_ASPECT);
  return { width, height: width / CROP_ASPECT };
}

/**
 * The zoom at which the whole photograph is inside the crop - below 1 for
 * anything that is not already 16:10, because the crop has to grow past the
 * short edge of the picture to take all of the long one in. Zooming out this
 * far keeps every last bit of the photograph, at the cost of padded bars.
 */
function fitZoom(
  source: CropSource,
  widest: { width: number; height: number },
): number {
  return Math.min(widest.width / source.width, widest.height / source.height);
}

/**
 * Choose the part of a photograph that is kept.
 *
 * Everywhere a treatment photo appears it is landscape, and a photograph off a
 * phone is portrait, so a lot of it goes. Left to the CSS that means the middle
 * survives - which for a head-and-shoulders picture is a forehead. So the studio
 * frames it here instead: drag to move, the slider or the wheel to zoom.
 *
 * Zoom runs from the whole photograph, padded to landscape, to four times into
 * it - so a picture that should be shown entire need not be cropped at all.
 */
export function ImageCropper({
  source,
  subject,
  busy,
  error,
  onCancel,
  onConfirm,
}: ImageCropperProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    pointerId: number;
    x: number;
    y: number;
    centre: View["centre"];
  } | null>(null);

  const [frameWidth, setFrameWidth] = useState(0);
  const [view, setView] = useState<View>(() => ({
    zoom: 1,
    centre: { x: source.width / 2, y: source.height / 2 },
  }));

  const widest = useMemo(() => widestCrop(source), [source]);
  const minZoom = useMemo(() => fitZoom(source, widest), [source, widest]);

  /** Applies a change, then pulls it back inside the picture. */
  const apply = useCallback(
    (change: (current: View) => View) => {
      setView((current) => {
        const next = change(current);
        const zoom = clamp(next.zoom, minZoom, MAX_ZOOM);
        const width = widest.width / zoom;
        const height = widest.height / zoom;
        return {
          zoom,
          centre: {
            x: clamp(next.centre.x, width / 2, source.width - width / 2),
            y: clamp(next.centre.y, height / 2, source.height - height / 2),
          },
        };
      });
    },
    [minZoom, source.height, source.width, widest],
  );

  const rect: CropRect = useMemo(() => {
    const width = widest.width / view.zoom;
    const height = widest.height / view.zoom;
    return {
      x: view.centre.x - width / 2,
      y: view.centre.y - height / 2,
      width,
      height,
    };
  }, [view, widest]);

  /** Displayed pixels per source pixel. Zero until the frame has been measured. */
  const k = frameWidth ? frameWidth / rect.width : 0;

  /** Zoomed out far enough that the crop reaches past the picture. */
  const padded =
    rect.x < -0.5 ||
    rect.y < -0.5 ||
    rect.x + rect.width > source.width + 0.5 ||
    rect.y + rect.height > source.height + 0.5;

  // A modal dialog, for the focus trap and the top layer - both tedious to
  // reproduce by hand and easy to get subtly wrong.
  useEffect(() => {
    const dialog = dialogRef.current;
    dialog?.showModal();
    return () => dialog?.close();
  }, []);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;
    setFrameWidth(frame.clientWidth);
    const observer = new ResizeObserver(() => setFrameWidth(frame.clientWidth));
    observer.observe(frame);
    return () => observer.disconnect();
  }, []);

  // React registers wheel at the root as passive, where preventDefault is
  // ignored, so this one is attached by hand to stop the page scrolling with it.
  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const factor = event.deltaY < 0 ? 1 + ZOOM_STEP : 1 - ZOOM_STEP;
      apply((current) => ({ ...current, zoom: current.zoom * factor }));
    };
    frame.addEventListener("wheel", onWheel, { passive: false });
    return () => frame.removeEventListener("wheel", onWheel);
  }, [apply]);

  function startDrag(event: React.PointerEvent<HTMLDivElement>) {
    if (busy || !k) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      centre: view.centre,
    };
  }

  function drag(event: React.PointerEvent<HTMLDivElement>) {
    const start = dragRef.current;
    if (!start || start.pointerId !== event.pointerId) return;
    // Dragging the photograph to the right shows what lies further left in it.
    apply((current) => ({
      ...current,
      centre: {
        x: start.centre.x - (event.clientX - start.x) / k,
        y: start.centre.y - (event.clientY - start.y) / k,
      },
    }));
  }

  function endDrag(event: React.PointerEvent<HTMLDivElement>) {
    if (dragRef.current?.pointerId === event.pointerId) dragRef.current = null;
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (busy || !k) return;
    const step = (NUDGE * frameWidth) / k;
    const pan = (dx: number, dy: number) =>
      apply((current) => ({
        ...current,
        centre: {
          x: current.centre.x + dx * step,
          y: current.centre.y + dy * step,
        },
      }));

    switch (event.key) {
      case "ArrowLeft":
        pan(-1, 0);
        break;
      case "ArrowRight":
        pan(1, 0);
        break;
      case "ArrowUp":
        pan(0, -1);
        break;
      case "ArrowDown":
        pan(0, 1);
        break;
      case "+":
      case "=":
        apply((current) => ({
          ...current,
          zoom: current.zoom * (1 + ZOOM_STEP),
        }));
        break;
      case "-":
      case "_":
        apply((current) => ({
          ...current,
          zoom: current.zoom * (1 - ZOOM_STEP),
        }));
        break;
      default:
        return;
    }
    event.preventDefault();
  }

  return (
    <dialog
      ref={dialogRef}
      className="cropper"
      aria-labelledby="cropper-title"
      onCancel={(event) => {
        event.preventDefault();
        if (!busy) onCancel();
      }}
    >
      <h2 className="cropper__title" id="cropper-title">
        Position the photograph
      </h2>
      <p className="cropper__lede">
        Drag the picture to choose what is kept for {subject}. Everything
        outside the frame is trimmed away - or zoom out until all of it is
        inside the frame, and nothing is.
      </p>

      <div
        ref={frameRef}
        className="cropper__frame"
        tabIndex={0}
        role="group"
        aria-label="Photograph position. Drag it, or use the arrow keys to move and the plus and minus keys to zoom."
        onPointerDown={startDrag}
        onPointerMove={drag}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onKeyDown={onKeyDown}
      >
        {k > 0 && (
          <img
            className="cropper__image"
            src={source.previewUrl}
            alt=""
            draggable={false}
            style={{
              width: `${source.width * k}px`,
              height: `${source.height * k}px`,
              transform: `translate(${-rect.x * k}px, ${-rect.y * k}px)`,
            }}
          />
        )}
        {/* The treatment menu shows a narrower window than this onto the stored
            photograph, so the part certain to survive is worth marking. */}
        <div className="cropper__safe" aria-hidden="true" />
      </div>

      <label className="cropper__zoom">
        <span className="cropper__zoom-label">Zoom</span>
        <input
          type="range"
          min={minZoom}
          max={MAX_ZOOM}
          step={0.01}
          value={view.zoom}
          disabled={busy}
          onChange={(event) =>
            apply((current) => ({
              ...current,
              zoom: Number(event.target.value),
            }))
          }
        />
        <span className="cropper__zoom-value">{view.zoom.toFixed(1)}×</span>
      </label>

      {/* Zoomed out past the picture, so the upload will carry bars. Said only
          then, because the rest of the time there is nothing to explain. */}
      {padded && (
        <p className="field-hint">
          Zoomed out past the edges of the photograph. All of it is kept, and
          the space around it is filled with the colour of the page it sits on.
        </p>
      )}

      <p className="field-hint">
        The dashed box is the part shown on the treatment menu, which is a
        narrower window than the one stored, so keep the subject inside it.
      </p>

      {error && <p className="notice notice--error">{error}</p>}

      <div className="cropper__actions">
        <button
          type="button"
          className="btn btn--ghost"
          disabled={busy}
          onClick={onCancel}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn"
          disabled={busy}
          onClick={() => onConfirm(rect)}
        >
          {busy ? "Uploading…" : "Use this crop"}
        </button>
      </div>
    </dialog>
  );
}
